import { useCallback, useContext, useEffect, useMemo } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import Clickable from 'components/Common/Clickable/Clickable'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import ReserveForZeroPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import ReserveForZeroAndDepositPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import CurrencyContext from 'contexts/currencyContext'
import ModalContext from 'contexts/ModalContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import config from 'constants/config'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import { max } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { feesToObject } from 'lib/offer/pricing'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import Pill from 'LuxKit/Pill'
import TextLink from 'LuxKit/TextLink'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineClockDollarIcon from 'LuxKit/Icons/line/LineClockDollarIcon'
import PackageSelectionBurnPrice from 'components/OfferPage/packageSelection/PackageSelectionBurnPrice'
import type { PackageSelectionPricing } from 'components/OfferPage/packageSelection/packageSelectionPricing'
import type { FlashHotelPackageDisplayedPricing } from '../getFlashHotelPackageDisplayedPricing'
import { getDiscountPillVisibilityFlags } from '../flashHotelDiscountPillVisibilityUtils'
import FlashPackageSelectionPrice from './FlashPackageSelectionPrice'

interface Props {
  offer: App.HotelOffer
  packageView: App.OfferPackageOptionView
  rooms: Array<OfferPageRoom>
  occupancyOverride?: Array<OfferPageRoom>
  checkIn?: string
  fetching: boolean
  isSpoofed: boolean
  defaultDepositAmountPercentage: number
  priceMode: OfferPagePriceMode
  quoteScope?: 'from' | 'selected'
  selectionPricing: PackageSelectionPricing
  onDisplayedPricingChange?: (pricing: FlashHotelPackageDisplayedPricing | undefined) => void
}

/** Hotel-only Option D consumes the selection's quote; Control keeps its original pricing owner. */
function FlashPackageSelectionPriceDetails({ offer, packageView, rooms, checkIn, fetching, isSpoofed, defaultDepositAmountPercentage, priceMode, quoteScope, selectionPricing, onDisplayedPricingChange = noop }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const currentCurrency = useContext(CurrencyContext)
  const { package: pkg, pricing, memberPricing } = packageView
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const fees = feesToObject(pricing.fees)
  const priceWithoutPropertyFees = pricing.price - (fees.property ?? 0)
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms, pkg, offer), [offer, pkg, rooms])
  const otherFees = useMemo(() => ({
    propertyFees: packageView.propertyFees,
    extraGuestsSurcharge: packageView.extraGuestsSurchargePayableAtProperty,
  }), [packageView.extraGuestsSurchargePayableAtProperty, packageView.propertyFees])
  const showPriceDetails = !!fees.taxesAndFees || !!fees.property || config.agentHub.isEnabled
  const showMemberPrice = !!memberPricing && memberPricing.price > 0
  const extraGuestSurchargeLabel = intl.formatMessage({
    id: 'c49bMGV6K-aAi1z169XQ',
    defaultMessage: '+ Extra guest surcharge',
    description: 'Description for extra guest surcharge on the offer detail page',
  })
  const shouldShowHotelValue = typeof pricing.value === 'number' && pricing.value > pricing.price && pkg.shouldDisplayValue
  const discountPercent = max([pricing.discountPercent, 0])
  const discountPillVisibility = getDiscountPillVisibilityFlags({
    offerDiscountPillHidden: offer.isDiscountPillHidden,
    shouldDisplayValue: pkg.shouldDisplayValue,
    bundledWithFlightsOnly: false,
    hasFlightPrice: false,
    pricing,
    discountPercent,
    hotelValueVisible: shouldShowHotelValue,
    flightsValueVisible: false,
    charterValueVisible: false,
  })
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const showFlexiblePaymentTag = isSpoofed && isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, pricing.price)
  const showReserveForZero = (config.RESERVE_FOR_ZERO_ENABLED || isSpoofed) && isPackageEligibleForZeroReservation(offer, checkInMoment, pkg.roomRate?.cancellationPolicy?.type)
  const onFlexPaymentPolicyOpen = useCallback(() => {
    showModal(<ReserveForZeroAndDepositPolicyModal
      depositAmountPercentage={depositAmountPercentage}
      offerType={offer.type}
      depositThresholds={offer.depositThresholds}
      depositAmount={offer.depositAmount}
    />)
  }, [depositAmountPercentage, offer.depositAmount, offer.depositThresholds, offer.type, showModal])
  const openReserveForZeroPolicyModal = useCallback(() => {
    showModal(<ReserveForZeroPolicyModal />)
  }, [showModal])
  const selectedPricing = selectionPricing.quantity > 0 ? selectionPricing : undefined
  const selectedPointsPrice = selectedPricing?.payment && <PackageSelectionBurnPrice
    price={selectedPricing.price}
    memberPrice={selectedPricing.memberPrice}
    payment={selectedPricing.payment}
    offerConfig={offer.luxPlus}
  />
  const convertToDisplay = useMemo(() => getPriceDisplayFunction(priceMode, rooms), [priceMode, rooms])
  const hotelPrice = selectedPricing?.price ?? pricing.price
  const displayPrice = convertToDisplay(hotelPrice)
  const memberPrice = selectedPricing?.memberPrice ?? (memberPricing?.price || undefined)
  const displayMemberPrice = memberPrice !== undefined ? convertToDisplay(memberPrice) : undefined
  const displayValue = shouldShowHotelValue && pricing.value > hotelPrice ? convertToDisplay(pricing.value) : undefined
  const displayedPricing = useMemo(() => fetching ? undefined : {
    nonMemberPrice: displayPrice,
    memberPrice: displayMemberPrice,
    value: displayValue ?? displayPrice,
  }, [displayMemberPrice, displayPrice, displayValue, fetching])
  useEffect(() => {
    onDisplayedPricingChange(displayedPricing)
  }, [displayedPricing, onDisplayedPricingChange])

  return <FlashPackageSelectionPrice
    offer={offer}
    packageView={packageView}
    checkIn={checkIn}
    quoteScope={quoteScope}
    fetching={fetching}
    selectedQuantity={selectedPricing?.quantity}
    showPointsPrice={!!selectedPointsPrice}
    pointsPrice={selectedPointsPrice}
    displayPrice={displayPrice}
    displayMemberPrice={displayMemberPrice}
    displayValue={displayValue}
    showDiscount={discountPillVisibility.hotelValueDiscountPillVisible || discountPillVisibility.topDiscountPillVisible}
    discountPercent={discountPercent}
    priceMode={priceMode}
    disclosures={<>
      {showIncludedGuestsExceeded && <AdditionalGuestsPopup
        complex={false}
        description={extraGuestSurchargeLabel}
        modalContent={pkg.roomPolicyDescription}
      />}
      {showPriceDetails && <OfferPriceDetailsRow
        trigger="price-row"
        triggerSize="M"
        offerPage
        offer={offer}
        duration={pkg.duration}
        propertyFees={fees.property}
        hotelPrice={priceWithoutPropertyFees}
        otherFees={otherFees}
        rooms={rooms}
        extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
        dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
        hotelMemberPrice={packageView.hotelMemberPrice}
        showMemberPrice={showMemberPrice}
      />}
      {showReserveForZero && <TextLink colour="highlight-primary-dark" size="small" weight="bold" endIcon={<LineInfoCircleIcon colour="primary" />} onClick={openReserveForZeroPolicyModal}>
        {intl.formatMessage({ id: '3wQ4yMmF7-2EH4zxp8u0', defaultMessage: 'Reserve now for free', description: 'Hyperlink text to reserve now for free on the offer detail page' })}
      </TextLink>}
      {showFlexiblePaymentTag && <Clickable onClick={onFlexPaymentPolicyOpen}><Pill startIcon={<LineClockDollarIcon />}>
        {intl.formatMessage({ id: '_WfvoKPJ644Skcza0yYb', defaultMessage: 'Flexible payment options', description: 'Label for flexible payment options during the checkout and payment process' })}
      </Pill>
      </Clickable>}
    </>}
  />
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  offer: state.offer,
  rooms: ownProps.occupancyOverride ?? state.rooms,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isSpoofed: appState.auth.account.isSpoofed,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  priceMode: state.priceMode,
}), FlashPackageSelectionPriceDetails)
